import { Flex, Checkbox } from "@chakra-ui/react";
import CollapsibleCard from "./components/CollapsibleCard";
import DuracaoReserva from "./components/DuracaoReserva";
import { TipoIntervaloPeriodo } from "core/features/area-comum/typings";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import {
  useForm,
  Control,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form";
import PeriodoAntecendenciaReserva from "./components/PeriodoAntecendenciaReserva";
import TempoEntreReserva from "./components/TempoEntreReserva";
import Field from "ui/components/Input/Field";
import BloqueioPeriodoAntesDepoisReserva from "./components/BloqueioPeriodoAntesDepoisReserva";
import useAreaComumRegrasReserva from "core/features/area-comum/hooks/useAreaComumRegrasReserva";
import useAreaComumSaveRegraReserva from "core/features/area-comum/hooks/useAreaComumSaveRegraReserva";
import { useLoading } from "components/GlobalLoading";
import { useEffect, useCallback, useRef } from "react";
import {
  AreaComumRegrasReserva,
  BloqueioAntesDepoisReservaItem,
} from "core/features/area-comum/typings";
import useAreaComumRegraReservaExcluirBloqueio from "core/features/area-comum/hooks/useAreaComumRegraReservaExcluirBloqueio";
import ParametrizarVencimentoBoletoReserva from "./components/ParametrizarVencimentoBoletoReserva";
import PeriodoAntecedenciaCancelarReserva from "./components/PeriodoAntecedenciaCancelarReserva";
import LimiteReservaPeriodo from "./components/LimiteReservaPeriodo";

type OnSaveRegraParam = Partial<
  Omit<AreaComumRegrasReserva, "bloqueioAntesDepoisReserva"> & {
    bloqueioAntesDepoisReserva: Partial<BloqueioAntesDepoisReservaItem>;
  }
>;
export interface RegraReservaComponentProps {
  control: Control<AreaComumRegrasReserva, any>;
  watch: UseFormWatch<AreaComumRegrasReserva>;
  getNameLabel: (tipo: TipoIntervaloPeriodo) => string | undefined;
  onSaveRegra?: (value: OnSaveRegraParam, shouldRefetch?: boolean) => any;
  setValue?: UseFormSetValue<AreaComumRegrasReserva>;
}

const defaultValueRegraGeralAntesDepois: BloqueioAntesDepoisReservaItem = {
  diaSemana: null,
  periodoAntes: null,
  periodoDepois: null,
  tipoIntervaloPeriodoAntes: null,
  tipoIntervaloPeriodoDepois: null,
};

export default function RegrasReserva({ id }: { id: string }) {
  const { tenant } = useCurrentTenant();

  const isLoadingSaveRef = useRef(false);

  const {
    data: regrasReserva,
    isLoading: isLoadingRegras,
    isFetching: isFetchingRegras,
    refetch,
  } = useAreaComumRegrasReserva(id, tenant.id);

  const { mutate: saveRegra, isLoading: isLoadingSave } =
    useAreaComumSaveRegraReserva(id, tenant.id);

  const { mutate: deleteBloqueio, isLoading: isLoadingDeleteBloqueio } =
    useAreaComumRegraReservaExcluirBloqueio(id, tenant.id);

  const { control, reset, watch, setValue } = useForm<AreaComumRegrasReserva>({
    defaultValues: {
      bloqueioAntesDepoisReserva: [defaultValueRegraGeralAntesDepois],
    },
  });

  const getNameLabel = (tipo: TipoIntervaloPeriodo) => {
    if (tipo === TipoIntervaloPeriodo.Dia) {
      return "Dia(s)";
    }
    if (tipo === TipoIntervaloPeriodo.Hora) {
      return "Hora(s)";
    }
    if (tipo === TipoIntervaloPeriodo.Mes) {
      return "Mês(es)";
    }
    if (tipo === TipoIntervaloPeriodo.Minuto) {
      return "Minuto(s)";
    }
    if (tipo === TipoIntervaloPeriodo.Semana) {
      return "Semana(s)";
    }
    if (tipo === TipoIntervaloPeriodo.Ano) {
      return "Ano(s)";
    }
  };

  useEffect(() => {
    if (regrasReserva) {
      const regraBloqueioGeralAntesDepois =
        regrasReserva.bloqueioAntesDepoisReserva.find(
          (regra) => regra.diaSemana === null
        );

      const bloqueioAntesDepoisReserva = regraBloqueioGeralAntesDepois
        ? regrasReserva.bloqueioAntesDepoisReserva
        : [
            defaultValueRegraGeralAntesDepois,
            ...regrasReserva.bloqueioAntesDepoisReserva,
          ];

      reset({ ...regrasReserva, bloqueioAntesDepoisReserva });
    }
  }, [regrasReserva, reset]);

  useLoading(
    isLoadingRegras ||
      isFetchingRegras ||
      isLoadingSave ||
      isLoadingDeleteBloqueio
  );

  const onSaveRegra = useCallback(
    (regra: OnSaveRegraParam, shouldRefetch?: boolean) => {
      if (!isLoadingSaveRef.current) {
        isLoadingSaveRef.current = true;
        saveRegra(regra, {
          onSuccess: () => {
            if (shouldRefetch) {
              refetch();
            }
          },
          onSettled: () => {
            isLoadingSaveRef.current = false;
          },
        });
      }
    },
    [refetch, saveRegra]
  );

  const onExcluirBloqueio = useCallback(
    (diaSemana: number) => {
      deleteBloqueio(diaSemana, {
        onSuccess: () => {
          refetch();
        },
      });
    },
    [deleteBloqueio, refetch]
  );

  const form = watch();

  return (
    <>
      <Flex flexDir="column" gap={4}>
        <CollapsibleCard
          titleCard="Regra 1: Duração de uma reserva"
          subtitleCard="Período de tempo que dura a reserva da área comum."
          onSave={() => {
            onSaveRegra({
              tipoIntervaloDuracaoReserva:
                form.tipoIntervaloDuracaoReserva ?? null,
              duracaoReserva: form.duracaoReserva ?? null,
            });
          }}
          children={
            <DuracaoReserva
              control={control}
              watch={watch}
              getNameLabel={getNameLabel}
              onSaveRegra={onSaveRegra}
            />
          }
        />
        <CollapsibleCard
          titleCard="Regra 2: Tempo entre uma reserva e outra para a mesma unidade"
          subtitleCard="Período de tempo em que a mesma unidade pode reservar novamente a área comum."
          onSave={() => {
            onSaveRegra({
              tipoIntervaloEntreReserva: form.tipoIntervaloEntreReserva ?? null,
              intervaloEntreReserva: form.intervaloEntreReserva ?? null,
            });
          }}
          children={
            <TempoEntreReserva
              control={control}
              watch={watch}
              getNameLabel={getNameLabel}
              onSaveRegra={onSaveRegra}
            />
          }
        />
        <CollapsibleCard
          titleCard="Regra 3: Período de antecedência para fazer a reserva"
          subtitleCard="Indica o prazo máximo e mínimo em que o morador pode realizar a reserva, ou seja, quando podem ser iniciadas as reservas e até quanto tempo antes elas podem ser feitas."
          onSave={() => {
            onSaveRegra({
              tipoIntervaloMinimoAntecendencia:
                form.tipoIntervaloMinimoAntecendencia ?? null,
              minimoAntecendencia: form.minimoAntecendencia ?? null,
              tipoIntervaloMaximoAntecendencia:
                form.tipoIntervaloMaximoAntecendencia ?? null,
              maximoAntecendencia: form.maximoAntecendencia ?? null,
            });
          }}
          children={
            <PeriodoAntecendenciaReserva
              control={control}
              watch={watch}
              getNameLabel={getNameLabel}
              onSaveRegra={onSaveRegra}
            />
          }
        />
        <CollapsibleCard
          titleCard="Regra 4: Bloquear datas antes e depois de uma reserva"
          subtitleCard="Permite informar quantos dias antes e quantos dias depois de uma reserva a área não poderá ser utilizada, dependendo do dia da semana."
          onSave={() => {
            if (form.bloqueioAntesDepoisReserva.length) {
              const bloqueio = form.bloqueioAntesDepoisReserva[0];

              onSaveRegra({
                bloqueioAntesDepoisReserva: bloqueio,
              });
            }
          }}
          children={
            <BloqueioPeriodoAntesDepoisReserva
              control={control}
              watch={watch}
              getNameLabel={getNameLabel}
              onSaveRegra={onSaveRegra}
              onExcluirBloqueio={onExcluirBloqueio}
            />
          }
        />
        <CollapsibleCard
          titleCard="Regra 5: Bloquear novas reservas caso haja uma reserva pendente ou confirmada ainda não utilizada nessa área"
          subtitleCard="Permite informar se um morador só poderá realizar uma nova reserva depois de usar a reserva anterior feita na mesma área comum."
          onSave={() => {
            onSaveRegra({
              realizarReservaAntesUtilizarOutra:
                form.realizarReservaAntesUtilizarOutra,
            });
          }}
          children={
            <Flex>
              <Field
                control={control}
                name="realizarReservaAntesUtilizarOutra"
                render={({ field }) => (
                  <Checkbox
                    isChecked={!!field.value}
                    onChange={() => {
                      field.onChange(!field.value);
                    }}
                  >
                    Sim
                  </Checkbox>
                )}
              />
            </Flex>
          }
        />
        <CollapsibleCard
          titleCard="Regra 6: Parametrizar vencimento do boleto da reserva"
          subtitleCard="Aqui você pode parametrizar em quantos dias o boleto da reserva irá vencer. Seja em relação ao dia em que foi criada a reserva ou em relação ao dia do evento."
          onSave={() => {
            onSaveRegra({
              tipoVencimentoCobranca: form.tipoVencimentoCobranca ?? null,
              diasVencimentoCobranca: form.diasVencimentoCobranca ?? null,
            });
          }}
          children={
            <ParametrizarVencimentoBoletoReserva
              control={control}
              watch={watch}
              getNameLabel={getNameLabel}
            />
          }
        />
        <CollapsibleCard
          titleCard="Regra 7: Período de antecedência para cancelar uma reserva"
          subtitleCard="Aqui você pode parametrizar o período mínimo antes de uma reserva para que a mesma possa ser cancelada."
          onSave={() => {
            onSaveRegra({
              tipoIntervaloAntecedenciaCancelarReserva:
                form.tipoIntervaloAntecedenciaCancelarReserva ?? null,
              antecedenciaCancelarReserva:
                form.antecedenciaCancelarReserva ?? null,
            });
          }}
          children={
            <PeriodoAntecedenciaCancelarReserva
              control={control}
              watch={watch}
              getNameLabel={getNameLabel}
            />
          }
        />
        <CollapsibleCard
          titleCard="Regra 8: Configurar limite de reservas"
          subtitleCard="Permite configurar o número máximo de reservas que uma unidade pode fazer em um dia, um mês e um ano."
          onSave={() => {
            onSaveRegra({
              limitesReservaPeriodo: form.limitesReservaPeriodo ?? [],
            });
          }}
          children={
            <LimiteReservaPeriodo
              control={control}
              watch={watch}
              getNameLabel={getNameLabel}
              setValue={setValue}
            />
          }
        />
        <CollapsibleCard
          titleCard="Regra 9: Liberar inadimplente para reservar esta area comum."
          subtitleCard="Esta opção libera o inadimplente para fazer reserva somente nessa área comum."
          onSave={() => {
            onSaveRegra({
              inadimplentePodeReservar: form.inadimplentePodeReservar,
            });
          }}
          children={
            <Flex>
              <Field
                control={control}
                name="inadimplentePodeReservar"
                render={({ field }) => (
                  <Checkbox
                    isChecked={!!field.value}
                    onChange={() => {
                      field.onChange(!field.value);
                    }}
                  >
                    Sim
                  </Checkbox>
                )}
              />
            </Flex>
          }
        />
      </Flex>
    </>
  );
}
